<script setup lang="ts">
import { computed } from 'vue'

import type { DeckCardInput } from '../../../deck/types'

import DeckPlayerBackButton from '../../components/buttons/DeckPlayerBackButton.vue'
import DeckPlayerNextButton from '../../components/buttons/DeckPlayerNextButton.vue'
import DeckPlayerRedoButton from '../../components/buttons/DeckPlayerRedoButton.vue'
import DeckPlayerResetButton from '../../components/buttons/DeckPlayerResetButton.vue'
import DeckPlayerSkipButton from '../../components/buttons/DeckPlayerSkipButton.vue'
import DeckPlayerUndoButton from '../../components/buttons/DeckPlayerUndoButton.vue'
import { postDeckPlayerReset } from '../../api/deckPlayerApi'
import { useDeckPlayerStore } from '../../store/useDeckPlayerStore'

export type DeckPlayerButtonType = 'next' | 'back' | 'skip' | 'undo' | 'redo' | 'reset'

const props = defineProps<{
  deckId: string
  button: DeckPlayerButtonType

  /** Optional: enables skip if button="skip" is used. */
  allowSkip?: boolean

  /** Optional: only needed to make Reset functional without a mounted host. */
  cards?: DeckCardInput[]

  /** Optional: button label override. */
  label?: string
}>()

const store = useDeckPlayerStore()
const session = computed(() => store.getSession(props.deckId))

const canBack = computed(() => (session.value?.queue?.length ?? 0) > 1)
const canNext = computed(() => (session.value?.queue?.length ?? 0) > 1)
const canUndo = computed(() => (session.value?.undo?.length ?? 0) > 0)
const canRedo = computed(() => (session.value?.redo?.length ?? 0) > 0)

const canReset = computed(() => {
  if (Array.isArray(props.cards)) return props.cards.length > 0
  return Boolean(session.value)
})

const canSkip = computed(() => props.allowSkip === true && canNext.value)

const next = () => store.next(props.deckId, 'next')
const back = () => store.back(props.deckId)
const skip = () => store.next(props.deckId, 'skip')
const undo = () => store.undo(props.deckId)
const redo = () => store.redo(props.deckId)
const reset = () => {
  void postDeckPlayerReset({ deckId: props.deckId, model: 'checkbox' }).catch(() => {
    // ignore (anonymous users / missing nonce / permission)
  })
  store.reset(props.deckId, props.cards)
}
</script>

<template>
  <DeckPlayerBackButton
    v-if="props.button === 'back'"
    :disabled="!canBack"
    :label="props.label"
    @click="back"
  />
  <DeckPlayerUndoButton
    v-else-if="props.button === 'undo'"
    :disabled="!canUndo"
    :label="props.label"
    @click="undo"
  />
  <DeckPlayerRedoButton
    v-else-if="props.button === 'redo'"
    :disabled="!canRedo"
    :label="props.label"
    @click="redo"
  />
  <DeckPlayerSkipButton
    v-else-if="props.button === 'skip'"
    :disabled="!canSkip"
    :label="props.label"
    @click="skip"
  />
  <DeckPlayerNextButton
    v-else-if="props.button === 'next'"
    :disabled="!canNext"
    :label="props.label"
    @click="next"
  />
  <DeckPlayerResetButton v-else :disabled="!canReset" :label="props.label" @click="reset" />
</template>
